import React, { useEffect, useRef, useState } from "react";
import { CustomAreaChart, Loader } from "@/components";
import { FeeResponse } from "@/types/Fee";
import GenericApi from "@/api/genericApi";
import {
  CurrencyQueryKey,
  StatisticsQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse } from "@/types/Api";
import { useQuery } from "@tanstack/react-query";
import { Currency } from "@/types/Currency";
import axios from "axios";
import { getUserToken } from "@/utils/localStorage";
import { AnimatePresence, motion } from "framer-motion";
import { isBitramp } from "@/utils/utils";

interface Props {
  startDate: number;
}

const ReportsGraphAreaFee: React.FC<Props> = ({ startDate }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("BTC");
  const [openCurency, setOpenCurency] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpenCurency(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getOtherdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - startDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery<
    ApiResponse<Currency[]>
  >({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  const handleDownload = async () => {
    try {
      const today = getTodayDate();
      const yesterday = getOtherdayDate();
      const response = await axios.get(
        `${GenericApi.baseUrl}/statistics/fee/download?start_date=${yesterday}&end_date=${today}&currency=${selectedCurrency}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `invoice_fee.xlsx`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading EXCEL:", error);
    }
  };

  const { data: feeReport, isLoading: isLoadingFeeReport } = useQuery<
    ApiResponse<FeeResponse[]>
  >({
    queryFn: async () => {
      const today = getTodayDate();
      const yesterday = getOtherdayDate();
      return GenericApi.get<FeeResponse[]>(
        `${ApiRoutes.STATISTICS}/fee/${
          startDate > 30 ? "monthly" : "daily"
        }?start_date=${yesterday}&end_date=${today}&currency=${selectedCurrency}`
      );
    },
    queryKey: [StatisticsQueryKey, selectedCurrency, startDate],
  });

  const transformData = (data: FeeResponse[]) => {
    return data.map((item) => ({
      name: item.fee_date,
      uv: Number(item.fee),
    }));
  };

  const [chartData, setChartData] = useState<{ name: string; uv: number }[]>(
    []
  );

  useEffect(() => {
    if (feeReport?.data) {
      setChartData(transformData(feeReport.data));
    }
  }, [feeReport]);

  let totalFee = 0;

  if (feeReport?.data) {
    for (let i = 0; i < feeReport.data.length; i++) {
      totalFee += feeReport.data[i].fee;
    }
  }

  const cleanedFee = parseFloat(totalFee.toString()).toFixed(13);

  if (isLoadingFeeReport || isLoadingCurrencies) {
    return <Loader />;
  }

  return (
    <div className="w-full min-h-[33.6rem] flex flex-col" ref={reportRef}>
      <div
        className={` p-3 rounded-lg flex flex-col ${
          isBitramp() ? "!bg-[#2B2B2B] border !border-[#464646]" : "bg-white"
        }`}
      >
        <hr
          className={`border-[0.3px] opacity-50  my-3 px-5 ${
            isBitramp() ? "border-[#464646]" : "border-[#EFEFEF]"
          }`}
        />
        <div className="flex justify-between items-center px-5">
          <p
            className={`Subtitle   ${
              isBitramp() ? "text-[#D1D1D1]" : "dark-gray"
            }`}
          >
            Volume Balance
          </p>
          <p
            className={`Subtitle   ${
              isBitramp() ? "text-[#D1D1D1]" : "dark-gray"
            }`}
          >
            Currency
          </p>
        </div>
        <div className="flex justify-between items-center px-5"  ref={wrapperRef}>
          <p className="Title dark-blue semi-bold">{cleanedFee.slice(0, 13)}</p>
          <div
            className={`relative  py-2 Container border-none focus:ring-0  cursor-pointer w-fit ps-4 pe-8 Input semi-bold dark-blue Subtitle ${
              isBitramp() ? "!bg-[#2b2b2b]" : ""
            }`}
          >
            <div
              onClick={() => setOpenCurency(!openCurency)}
              className="h-full flex justify-start items-center gap-2 cursor-pointer"
            >
              {selectedCurrency}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={isBitramp() ? "#fff" : "#737791"}
                />
              </svg>
            </div>

            <AnimatePresence>
              {openCurency && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className={`absolute top-full left-0 w-full Container pshadow mt-1 z-10 ${
                    isBitramp()
                      ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                      : ""
                  }`}
                >
                  {currencies?.data?.map((currency) => (
                    <div
                      key={currency.symbol}
                      onClick={() => {
                        setSelectedCurrency(currency?.symbol ?? "");
                        setOpenCurency(false);
                      }}
                      className={`px-4 py-2  hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2 ${
                        isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                      }`}
                    >
                      {currency.symbol}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="my-4" />
        <CustomAreaChart customData={chartData} currency={selectedCurrency} />
      </div>
      <button
        onClick={handleDownload}
        className={`${
          isBitramp() ? "Upload_button_bitramp" : "Primary_button"
        }  w-fit mt-4 `}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="18"
          viewBox="0 0 14 18"
          fill="none"
        >
          <g clip-path="url(#clip0_3058_8281)">
            <path
              d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
              fill={isBitramp() ? "#262626" : "white"}
            />
            <path
              d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
              fill={isBitramp() ? "#262626" : "white"}
            />
          </g>
          <defs>
            <clipPath id="clip0_3058_8281">
              <rect width="14" height="18" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <div className={`${isBitramp() ? "text-[#262626]" : ""} `}>
          Download Excel
        </div>
      </button>
    </div>
  );
};

export default ReportsGraphAreaFee;
